/**
 * Public page locale strings — loaded at runtime, no build-time bundling needed.
 * Each locale is a flat key→string map. Falls back to 'en' for any missing key.
 *
 * Supported locales (12):
 *   en, es, fr, de, it, pt, ru, ja, ar, hi, ko, tr
 *
 * RTL locales: ar
 */

export type PublicLocaleKey =
  | 'page_not_found'
  | 'page_not_found_desc'
  | 'page_unpublished_title'
  | 'page_unpublished_desc'
  | 'protected_page'
  | 'password_protected_desc'
  | 'enter_password'
  | 'incorrect_password'
  | 'unlock_page'
  | 'age_restricted'
  | 'age_restricted_desc'
  | 'age_confirm'
  | 'go_back'
  | 'form_name_placeholder'
  | 'form_email_placeholder'
  | 'form_message_placeholder'
  | 'form_sent'
  | 'form_error'
  | 'form_preview_not_saved'
  | 'form_preview_thank_you'
  | 'form_thank_you'
  | 'form_sent_desc'
  | 'form_sending'
  | 'form_submit'
  | 'payment_not_configured'
  | 'payment_preview_not_started'
  | 'pay'
  // P3-2 tip jar keys (Stripe Connect donation widget)
  | 'tip_jar_headline'
  | 'tip_jar_send_tip'
  | 'tip_jar_custom'
  | 'tip_jar_message_placeholder'
  | 'tip_jar_thank_you'
  | 'tip_jar_not_configured'
  | 'tip_jar_pick_amount'
  | 'tip_jar_preview_not_started'
  | 'tip_jar_processing'
  | 'hours_none'
  | 'hours_heading'
  | 'closed'
  | 'map_no_location'
  | 'map_title'
  | 'made_with'
  | 'translation_unavailable'
  | 'choose_language'
  | 'product_buy_now'
  | 'product_out_of_stock';

export type PublicLocaleStrings = Record<PublicLocaleKey, string>;

export const SUPPORTED_LOCALES: { code: string; label: string; nativeLabel: string; rtl?: boolean }[] = [
  { code: 'en', label: 'English',    nativeLabel: 'English' },
  { code: 'es', label: 'Spanish',    nativeLabel: 'Español' },
  { code: 'fr', label: 'French',     nativeLabel: 'Français' },
  { code: 'de', label: 'German',     nativeLabel: 'Deutsch' },
  { code: 'it', label: 'Italian',    nativeLabel: 'Italiano' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português' },
  { code: 'ru', label: 'Russian',    nativeLabel: 'Русский' },
  { code: 'ja', label: 'Japanese',   nativeLabel: '日本語' },
  { code: 'ar', label: 'Arabic',     nativeLabel: 'العربية', rtl: true },
  { code: 'hi', label: 'Hindi',      nativeLabel: 'हिन्दी' },
  { code: 'ko', label: 'Korean',     nativeLabel: '한국어' },
  { code: 'tr', label: 'Turkish',    nativeLabel: 'Türkçe' },
];

export const RTL_LOCALES = new Set(['ar', 'he']);

/** Lazy-loaded locale cache — imported JSON files kept in memory after first load. */
const cache: Partial<Record<string, PublicLocaleStrings>> = {};

// Load all JSON files in the current folder, Vite will statically analyze this
const localeModules = import.meta.glob('./*.json');

export async function loadLocale(locale: string): Promise<PublicLocaleStrings> {
  if (cache[locale]) return cache[locale]!;
  try {
    const importFn = localeModules[`./${locale}.json`];
    if (!importFn) throw new Error(`Locale ${locale} not found`);
    
    // Evaluate the import function for the specific locale
    const mod = (await importFn()) as { default: PublicLocaleStrings };
    cache[locale] = mod.default;
    return cache[locale]!;
  } catch {
    // Unknown locale — fall back to English
    if (locale !== 'en') return loadLocale('en');
    return {} as PublicLocaleStrings;
  }
}

/** Synchronous accessor — returns key as fallback if locale not yet loaded. */
export function t(strings: PublicLocaleStrings | null, key: PublicLocaleKey, fallback?: string): string {
  return strings?.[key] ?? fallback ?? key;
}

/**
 * Resolve i18n field value from block settings.
 * Looks for `{field}_i18n.{locale}` first, falls back to flat `{field}`.
 * This preserves full backward-compat with existing single-language pages.
 */
export function loc(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  settings: Record<string, any>,
  field: string,
  locale: string,
  defaultLocale = 'en',
): string {
  const i18nMap = settings[`${field}_i18n`];
  if (i18nMap && typeof i18nMap === 'object') {
    return (i18nMap[locale] ?? i18nMap[defaultLocale] ?? settings[field] ?? '') as string;
  }
  return (settings[field] ?? '') as string;
}
